import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import "driver.js/dist/driver.css";
import { actions as userActions } from "store/users";
import {
    FORCE_TOURS,
    TOUR_REPLAY_START_DELAY_MS,
    createTourDriver,
    isTourShown,
    useTourReplayGeneration,
} from "utils/tours";

const { saveUserMeta } = userActions;

const DASHBOARD_PATH = "/dashboard";

let tourHasRun = false;
let lastHandledReplayGeneration = 0;
let activeDashboardTourDriver = null;
let persistDashboardTourOnDestroy = false;

const destroyActiveDashboardTour = ({ suppressReplayHint = false } = {}) => {
    if (activeDashboardTourDriver) {
        if (suppressReplayHint) {
            activeDashboardTourDriver.suppressReplayHint?.();
        }
        activeDashboardTourDriver.destroy();
        activeDashboardTourDriver = null;
    }
};

/** The Sentry widget only exposes a shadow host; the megaphone itself lives inside the shadow root. */
const getFeedbackActor = () =>
    document.querySelector("#sentry-feedback")?.shadowRoot?.querySelector(".widget__actor") || null;

const TOUR_STEPS = [
    {
        element: "[data-dashboard-tour='create-new-model']",
        popover: {
            title: "Create New Model",
            description:
                "Click here to create a new house model. You'll be taken to the creation flow where you can set up your building and start drawing.",
        },
    },
    {
        element: "[data-dashboard-tour='model-list-item']",
        popover: {
            title: "Your Models",
            description:
                "Once you've created a model, it appears here in your list. Click on a model to open it and continue editing.",
        },
    },
    {
        element: "[data-dashboard-tour='model-actions']",
        popover: {
            title: "Model Options",
            description:
                "Use the '...' menu on any model to duplicate it, move it to another folder, or delete it. You can also select multiple items (Ctrl/Cmd + click or Shift + click) to delete or move several at once.",
        },
    },
    {
        element: getFeedbackActor,
        popover: {
            title: "Report an Issue",
            description:
                "Use the megaphone in the bottom-right corner to report bugs or send feedback. On a model page, this also flags the model for review.",
            side: "left",
            align: "end",
        },
    },
];

const TOUR_VISIBILITY_POLL_MS = 500;

const TARGET_UNAVAILABLE = "unavailable";
/** Painted but scrolled out of view — usable, since the step scrolls it into view. */
const TARGET_OFFSCREEN = "offscreen";
const TARGET_VISIBLE = "visible";

const resolveTourTarget = (element) =>
    (typeof element === "function" ? element() : document.querySelector(element)) || null;

const isPainted = (el) => {
    if (typeof el.checkVisibility === "function") {
        return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
    }

    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden" || Number(style.opacity) === 0) {
        return false;
    }
    const size = el.getBoundingClientRect();
    return size.width > 0 && size.height > 0;
};

const intersectsViewport = (rect) =>
    rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;

/** Whether something is drawn on top of the target (e.g. the email confirmation modal). */
const isCovered = (el, rect) => {
    const x = Math.min(Math.max(rect.left + rect.width / 2, 0), window.innerWidth - 1);
    const y = Math.min(Math.max(rect.top + rect.height / 2, 0), window.innerHeight - 1);
    const topEl = document.elementFromPoint(x, y);
    if (!topEl) return true;
    if (el === topEl || el.contains(topEl)) return false;

    // Elements inside a shadow root retarget to their host, so compare against that instead.
    const host = el.getRootNode()?.host;
    return !(host && (host === topEl || host.contains(topEl)));
};

const getTargetState = (el) => {
    if (!el || !isPainted(el)) return TARGET_UNAVAILABLE;

    // elementFromPoint only reads viewport coordinates, so off-screen targets can't be cover-tested.
    const rect = el.getBoundingClientRect();
    if (!intersectsViewport(rect)) return TARGET_OFFSCREEN;

    return isCovered(el, rect) ? TARGET_UNAVAILABLE : TARGET_VISIBLE;
};

const isFixed = (el) => Boolean(el) && window.getComputedStyle(el).position === "fixed";

const getTourTargets = () =>
    TOUR_STEPS.map((step) => {
        const el = resolveTourTarget(step.element);
        return { step, state: getTargetState(el), fixed: isFixed(el) };
    });

export default function DashboardTour({
    modelDirLoading = false,
    name = "",
    company = "",
    role = "",
    agreedToTerms = false,
}) {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const uid = useSelector((state) => state.user?.uid);
    const replayGeneration = useTourReplayGeneration("dashboard");
    const dashboardTourShown = useSelector((state) => state.user?.userMeta?.dashboardTourShown);

    const isOnboardingComplete = Boolean(name && company && role && agreedToTerms);
    const isReady = !modelDirLoading && isOnboardingComplete;
    const isDashboardRoute = pathname === DASHBOARD_PATH;

    // Tear down only when leaving the dashboard, not when load/model state changes.
    useEffect(() => {
        if (isDashboardRoute) return undefined;
        destroyActiveDashboardTour({ suppressReplayHint: true });
        tourHasRun = false;
        return undefined;
    }, [isDashboardRoute]);

    useEffect(() => {
        const isReplay = replayGeneration > lastHandledReplayGeneration;
        if ((!isReplay && isTourShown(dashboardTourShown)) || !isReady || !isDashboardRoute) return;

        if (isReplay) {
            lastHandledReplayGeneration = replayGeneration;
            destroyActiveDashboardTour({ suppressReplayHint: true });
            tourHasRun = false;
        }

        // Already running or already started this session — don't restart on dep churn.
        if (tourHasRun || activeDashboardTourDriver) return;

        let cancelled = false;
        let visibilityPollId = null;
        let awaitingVisibleTargets = false;
        const startDelayMs = replayGeneration > 0 ? TOUR_REPLAY_START_DELAY_MS : 2000;

        const driveTour = (steps) => {
            const shouldPersist = Boolean(uid) && !FORCE_TOURS;
            destroyActiveDashboardTour();
            persistDashboardTourOnDestroy = shouldPersist;
            const tourDriver = createTourDriver({
                stepCount: steps.length,
                onHighlightStarted: (element) => {
                    // Fixed targets (the feedback megaphone) don't move with the page, so scrolling
                    // would only shift the content behind the overlay.
                    if (!element || isFixed(element)) return;

                    // Animating a long distance would leave the popover behind, so jump instead.
                    const onScreen = intersectsViewport(element.getBoundingClientRect());
                    element.scrollIntoView({ block: "center", behavior: onScreen ? "smooth" : "auto" });
                },
                onDestroyed: () => {
                    activeDashboardTourDriver = null;
                    if (persistDashboardTourOnDestroy) {
                        dispatch(saveUserMeta({ uid, userMeta: { dashboardTourShown: true } }));
                    }
                    persistDashboardTourOnDestroy = false;
                },
            });
            activeDashboardTourDriver = tourDriver;
            tourDriver.setSteps(steps);
            tourDriver.drive();
        };

        const waitForVisibleStepsThenDrive = () => {
            if (cancelled || window.location.pathname !== DASHBOARD_PATH) {
                awaitingVisibleTargets = false;
                tourHasRun = false;
                return;
            }

            const targets = getTourTargets();
            // Keep waiting while an overlay (e.g. email confirmation) covers the page rather than
            // touring behind it. Fixed targets float above overlays, so they don't prove anything.
            if (!targets.some(({ state, fixed }) => state === TARGET_VISIBLE && !fixed)) {
                awaitingVisibleTargets = true;
                visibilityPollId = setTimeout(waitForVisibleStepsThenDrive, TOUR_VISIBILITY_POLL_MS);
                return;
            }

            // Off-screen targets (model rows pushed below the fold by folders) still get a step.
            const steps = targets.filter(({ state }) => state !== TARGET_UNAVAILABLE).map(({ step }) => step);

            awaitingVisibleTargets = false;
            driveTour(steps);
        };

        const startTour = () => {
            if (cancelled || tourHasRun || activeDashboardTourDriver) return;
            if (window.location.pathname !== DASHBOARD_PATH) return;
            tourHasRun = true;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (cancelled || window.location.pathname !== DASHBOARD_PATH) {
                        tourHasRun = false;
                        return;
                    }
                    setTimeout(() => {
                        if (cancelled || window.location.pathname !== DASHBOARD_PATH) {
                            tourHasRun = false;
                            return;
                        }
                        waitForVisibleStepsThenDrive();
                    }, isReplay ? 0 : 300);
                });
            });
        };

        const timeoutId = setTimeout(startTour, startDelayMs);
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
            clearTimeout(visibilityPollId);
            // Don't destroy a running tour here — that caused restarts on dep churn.
            // If we were still waiting for visible targets, allow a later effect to retry.
            if (awaitingVisibleTargets) {
                awaitingVisibleTargets = false;
                tourHasRun = false;
            }
        };
    }, [dashboardTourShown, dispatch, uid, isReady, isDashboardRoute, replayGeneration]);

    return null;
}
